import * as SignalR from '@microsoft/signalr';
import { NotificationsService } from '../api';

/**
 * This service sets up the SignalR connection to Azure SignalR.  The connection to the
 * local hub will result in a redirect to the Azure SignalR endpoint with an access
 * token generated by the middleware.
 */
class SignalRService {
  connection: SignalR.HubConnection | undefined;

  /**
   * Build and start the connection to Azure SignalR.
   * @param idToken An ID token if we are managing auth.
   */
  public async connect(idToken: string) {
    if (this.connection) {
      // Stop any current connection
      await this.connection.stop();
      this.connection = undefined;
    }

    // Create a new connection
    this.connection = new SignalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/notifications/`, {
        accessTokenFactory: () => idToken,
      })
      .configureLogging(SignalR.LogLevel.Error)
      .withAutomaticReconnect()
      .build();

    console.log('Connected SignalR');

    this.connection.on('Test', (msg: string) => {
      console.log(`Received: ${msg}`);
    });

    await this.connection.start();
  }

  /**
   * Registers a handler for a specific event type.
   * @param event The event name to subscribe to.
   * @param handler The callback handler which receives the message
   */
  public register(event: string, handler: (msg: string) => void) {
    this.connection?.off(event);
    this.connection?.on(event, handler);
  }

  /**
   * Joins the channel in SignalR to receive real time notifications.
   * @param workspaceId The ID of the workspace to start receiving notifications for.
   */
  public async joinWorkspaceGroup(workspaceId: string) {
    if (!this.connection) {
      console.error('No active SignalR connection');
      return;
    }

    console.log(
      `Joining user to workspace channel: ${workspaceId} with connection ID: ${this.connection.connectionId}`,
    );

    await NotificationsService.connectToWorkspace(
      workspaceId,
      this.connection?.connectionId ?? 'missing-connection-id',
    );
  }

  /**
   * Joins the channel in SignalR to receive real time notifications.
   * @param portfolioId The ID of the portfolio to start receiving notifications for.
   */
  public async joinPortfolioGroup(portfolioId: string) {
    if (!this.connection) {
      console.error('No active SignalR connection');
      return;
    }

    console.log(
      `Joining user to portfolio channel: ${portfolioId} with connection ID: ${this.connection.connectionId}`,
    );

    await NotificationsService.connectToPortfolio(
      portfolioId,
      this.connection?.connectionId ?? 'missing-connection-id',
    );
  }
}

export const signalRService = new SignalRService();
